import React, { useEffect } from "react";
import styles from "./NoAccessComponents.module.scss";
import { ReactComponent as ForbiddenIcon } from "./forbidden.svg";
import { ReactComponent as AccessDenied } from "./AccessDenied.svg";
import { ReactComponent as DeprecatedIcon } from "./Deprecated_service.svg";
import externals from "./../../../utils/externals";
import { Button, Card, Layout, Heading, Text, ExternalLink, Span } from "@netapp/bxp-design-system-react";
import { ConnectorIcon } from "@netapp/bxp-style/react-icons/Storage";
import { useHistory, useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { ReactComponent as ComingSoonIcon } from "./coming-soon.svg";
import { ReactComponent as UpgradeRequiredIcon } from "./upgrade-required.svg";
import { isNewTenancyEnabled } from "utils/localstorage";
import { useTranslation } from 'react-i18next';
import { appComponents as i18nNamespace } from '../../../i18n/namespace';
import { isVirtualTourEnabled } from "../../../offline/utils";
import { isNcl } from "utils/app/utils";
import usePermission from "utils/usePermission";

const AccessDeniedCardContent = ({ headingText, primaryText, secondaryText }) => {
    return <>
        <Heading level={20} style={{ marginTop: 16, textAlign: "center" }} bold>{headingText}</Heading>
        <Text style={{ marginTop: 16 }}>{primaryText}</Text>
        {secondaryText && <Text>{secondaryText}</Text>}
    </>
}

export const Forbidden = () => {
    const { t } = useTranslation(i18nNamespace);
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden} />
        <Heading color="text-title">{t("contact_administrator")}</Heading>
        <Text>{t(isNcl() ? "add_user_to_project_ncl" : "add_user_to_project")}</Text>
        <ExternalLink trackCategory="NoAccessPages" href={externals.saasFrontend} newTab large>{t("go_to_bluexp")}</ExternalLink>
    </section>
};

export const SaaSDisabled = () => {
    const { t } = useTranslation(i18nNamespace);
    const isTenancyV4 = isNewTenancyEnabled();
    const location = useLocation();
    const isMarketplaceRedirection = location.pathname.includes('/subscription-mapping');
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden} />
        <Heading color="text-title">{isTenancyV4 ? t("noaccess_to_organization") : t("noaccess_to_interface")}</Heading>
        <Text>{t("diabled_saas_interface")}</Text>
        {isMarketplaceRedirection && <Text>{t("click")} <b>{t("connector")}</b> {t("top_right_menu")} <b>{t("go_to_local_ui")}</b>.</Text>}
        <Text>{t("support_question")}<ExternalLink variant="text" href={externals.supportServiceUrl} newTab>{t("netapp_support_site")}</ExternalLink></Text>
        {isMarketplaceRedirection && <div className={styles.restrictedImage}></div>}
    </section>
};

export const ServiceNeedsSaaSIsDisabled = () => {
    const { t } = useTranslation(i18nNamespace);
    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <AccessDenied />
                    <AccessDeniedCardContent headingText={t("saas_disabled_heading_text")} primaryText={t("bluexp_account_disabled_primary_text")}
                        secondaryText={
                            <>
                                {t("support_question")}<ExternalLink variant="text" href={externals.supportServiceUrl} newTab>{t("netapp_support_site")}</ExternalLink>
                            </>
                        }
                    />
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

export const ServiceNeedsThirdPartyIsDisabled = () => {
    const { t } = useTranslation(i18nNamespace);
    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <AccessDenied />
                    <AccessDeniedCardContent headingText={t("thirdparty_services_enabled")} primaryText={t("disabled_thirdparty_services")} secondaryText={t("support_question")} />
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

export const NoContainerMessage = () => {
    const { t } = useTranslation(i18nNamespace);
    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <ConnectorIcon color='text' style={{ marginTop: 6, width: 40, height: 28 }} />
                    <AccessDeniedCardContent headingText={t("something_went_wrong")} primaryText={t("bluexp_preparing_connectors")} />
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

export const PullingContainerMessage = () => {
    const { t } = useTranslation(i18nNamespace);
    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <ConnectorIcon color='text' style={{ marginTop: 6, width: 40, height: 28 }} />
                    <AccessDeniedCardContent headingText={t("something_went_wrong")} primaryText={t("bluexp_preparing_connector")} />
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

export const UpgradeRequired = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(i18nNamespace);
    const provider = useSelector(state => state.tenancy.selectedAgent?.provider);

    let type = "instance type"

    if (provider === "gcp") {
        type = "machine type";
    } else if (provider === "azure") {
        type = "VM type";
    } else if (provider === "on-prem") {
        type = "VM"
    }

    return <section className={styles.container} style={{ paddingTop: 60 }}>
        <UpgradeRequiredIcon style={{ marginBottom: 13 }} />
        <Heading color="error" bold><Span style={{ textTransform: "capitalize" }}>{type}</Span> {t("change_required")}</Heading>
        <Text style={{ maxWidth: 485, textAlign: "center" }}>{t("need_to_change_type", { type: type })}</Text>
        <Button style={{ width: 152, marginTop: 32 }} large onClick={() => {
            dispatch({
                type: "CURRENT-CONNECTOR:OPEN-WEAK-CONNECTOR-DIALOG",
                track: "from-no-access-page"
            })
        }}>{t("learn_more")}</Button>
    </section>
};

export const NoLocalMessage = () => {
    const location = useLocation();
    const { t } = useTranslation(i18nNamespace);
    const selectedAccountId = useSelector(state => state.tenancy.selectedAccountId);
    const connectorId = useSelector(state => state.tenancy.selectedAgentId);
    const connectorIdSuffix = connectorId ? `&connectorId=${connectorId}` : "";
    const url = `${externals.saasFrontend}${location.pathname}?accountId=${selectedAccountId}${connectorIdSuffix}`;

    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <AccessDenied />
                    <Heading level={20} style={{ marginTop: 16, textAlign: "center" }} bold>{t("service_accessible_from_bluexp")}</Heading>
                    <Text style={{ marginTop: 16, marginBottom: 16 }}>{t("visit_bluexp_ui")}</Text>
                    <ExternalLink
                        large
                        href={url}
                        newTab
                    > {t("go_to_bluexp")}</ExternalLink>
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

export const ComingSoonMessage = () => {
    const { t } = useTranslation(i18nNamespace);
    return <section className={styles.container}>
        <ComingSoonIcon className={styles['coming-soon']} />
        <Heading>{t("service_not_accessible")}</Heading>
        <Text>{t("connector_deploy")}</Text>
    </section>
};

export const NoPermissionMessage = () => {
    const isTenancyV4 = isNewTenancyEnabled();
    const { t } = useTranslation(i18nNamespace);

    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <AccessDenied />
                    <AccessDeniedCardContent headingText={isVirtualTourEnabled() ? t("netapp_cosole_simulator_not_supported") : t("access_denied_header_text")} primaryText={isVirtualTourEnabled() ? "" : (isTenancyV4 ? t("contact_org_admin") : t("contact_account_admin"))} />
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

export const NotSupportedInSimulatorMode = () => {
    const { t } = useTranslation(i18nNamespace);

    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <AccessDenied />
                    <AccessDeniedCardContent headingText={t("netapp_cosole_simulator_not_supported")} primaryText="" />
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
};

let count = 0;

export const NeedSaaSRedirect = React.memo(() => {
    const location = useLocation();
    const { t } = useTranslation(i18nNamespace);
    const selectedAccountId = useSelector(state => state.tenancy.selectedAccountId);
    const connectorId = useSelector(state => state.tenancy.selectedAgentId);
    const url = `${externals.saasFrontend}${location.pathname}?accountId=${selectedAccountId}&connectorId=${connectorId}`;

    useEffect(() => {
        if (count < 1) {
            window.open(url, "_blank");
            count++;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <AccessDenied />
                    <Heading level={20} style={{ marginTop: 16, textAlign: "center" }} bold>{t("secure_connection_required")}</Heading>
                    <Text style={{ marginTop: 16, marginBottom: 16 }}>{t("accessible_from_bluexp")}</Text>
                    <ExternalLink
                        large
                        href={url}
                        newTab
                    >{t("go_to_bluexp")}</ExternalLink>
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
});

export const DeprecatedService = ({ currentService }) => {
    const { t } = useTranslation(i18nNamespace);
    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <DeprecatedIcon />
                    <Heading level={20} style={{ marginTop: 50, textAlign: "center" }} bold>{t("service_not_available", { currentService: currentService.name })}</Heading>
                    <Text style={{ marginTop: 16, marginBottom: 16 }}>{t("contact")}<ExternalLink variant="text" href={externals.supportServiceUrl} newTab>
                        {t("support")}
                    </ExternalLink> {t("encounter_issue")}</Text>
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
}

export const RouteNotFound = () => {
    const history = useHistory();
    const { t } = useTranslation(i18nNamespace);

    const goToHomePage = () => {
        history.replace({ pathname: "/home", hash: window.location.hash });
    };

    return <Layout.Container>
        <Layout.Grid centerContent>
            <Layout.GridItem lg={6}>
                <Card className={styles["card-container"]}>
                    <Heading level={20} style={{ marginTop: 50, textAlign: "center" }} bold>{t("incorrect_url_title")}</Heading>
                    <Text style={{ marginTop: 16, marginBottom: 16 }}>
                        {t("incorrect_url_message")}
                    </Text>
                    <Button large onClick={goToHomePage}>{t("go_to_home_page")}</Button>
                </Card>
            </Layout.GridItem>
        </Layout.Grid>
    </Layout.Container>
}

export const RoutePermissionGuard = ({ permissionKey, permissionType, permissionTypes, children }) => {
    const hasPermission = usePermission([permissionKey]);
    const isAllowed = hasPermission({
        permissionKey,
        permissionType,
        permissionTypes
    });
    return isAllowed ? children : <NoPermissionMessage />;
};
